import React, {useState, useEffect, useRef} from "react";
import moment from "moment";

import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {Dropdown} from "primereact/dropdown";
import {Calendar} from "primereact/calendar";
import {Message} from "primereact/message";

import {formatCurrency, formatDecimal, getHostName, getKeyValue, stripTrackingParams, urlValidator} from "../../utils/Utils";
import {toDateTimeString} from "../../utils/TimeUtil";

import {AppContext} from "../../HomePage";
import {ProductService} from "../../service/ProductService";
import {UserService} from "../../service/UserService";
import {IndexTemplate} from "../../utils/InlineComponents";
import {Tag} from "primereact/tag";
import {job_states} from "../../utils/Constants";
import ScrapeStatus from "./ScrapeStatus";

// A supplier's stock as the quantity itself. The shared StockTemplate shows 1 as
// a bare check mark, because for competitor rows 1 usually means "in stock,
// quantity unknown"; every supplier site reports the real quantity, so here a
// stock of 1 is one unit and is shown as such.
export const SupplierStockTemplate = (stock) => (stock && stock > 0)
    ? <Tag style={{maxWidth: "7em", minWidth: "3em", textAlign: 'center'}} severity="success" value={stock}/>
    : <Tag style={{maxWidth: "7em", minWidth: "3em", textAlign: 'center'}} severity="danger" icon="pi pi-times"/>;

// A seller's current price/stock live on its last price entry, matching the
// backend's `prices[-1]` read (see vitaminbox_min_price.py).
const lastPriceOf = (supplier) => {
    const prices = supplier.prices || [];
    return prices.length > 0 ? prices[prices.length - 1] : null;
};

// `expiration` is either a real date or null (no expiry at all, which is
// normal and must not render as an error).
const parseExpiration = (expiration) => {
    if (expiration === null || expiration === undefined || expiration === "")
        return null;
    const parsed = moment(expiration);
    return parsed.isValid() ? parsed : null;
};

// A known expiry sooner than expiration_min_months away. Such a seller can
// never feed the minimum price, so the tab hides it by default. No expiry
// (null) means the listing does not expire, which is never "soon".
export const isExpiringSoon = (supplier, expirationThreshold) => {
    const expiration = parseExpiration(supplier.expiration);
    return expiration !== null && expiration.isBefore(expirationThreshold);
};

// Mirrors VitaminboxSupplierMinPrice.get_eligible_value exactly
// (pricerest product/min_price/vitaminbox_min_price.py): a seller counts
// toward the minimum price when its last price has a value, is in stock, and
// its expiration -- when it has one -- is not sooner than
// expiration_min_months away.
const isEligibleForMinPrice = (supplier, expirationThreshold) => {
    const lastPrice = lastPriceOf(supplier);
    if (!lastPrice || lastPrice.value === null || lastPrice.value === undefined || !lastPrice.stock)
        return false;
    return !isExpiringSoon(supplier, expirationThreshold);
};

// Drives the check-price control's polling loop. A request is still pending
// while todo/in_progress; done and failed are both terminal (failed will
// never change on its own, so polling it further would just spin forever for
// nothing) and "no request at all" (never checked) is trivially not pending.
// Exported so this is tested directly (supplierActions.test.js) rather than
// through mounting the tab -- this is what makes the control re-enable itself
// once the backend reports done, instead of freezing on the one-shot value
// the triggering click first saw.
export const isSupplierCheckPending = (scraping_request) =>
    !!scraping_request && (scraping_request.state === job_states.todo || scraping_request.state === job_states.in_progress);

// How often to re-poll the backend for this product's last supplier-check
// state while one is pending. Matches ProductDetail.js's own conditional poll
// interval for competitor/product scraping requests.
const SUPPLIER_CHECK_POLL_MS = 15000;

const Supplier = (props) => {

    const {t, toast, setError} = React.useContext(AppContext);
    const productService = new ProductService();
    const userService = new UserService();

    const selectedProduct = props.selectedProduct;
    const campaign = props.campaign;
    const settings = campaign?.settings?.supplier_module || {};
    // Same defaults as VitaminboxSupplierMinPrice.calculate.
    const sellerCount = settings.seller_count || 3;
    const expirationMinMonths = settings.expiration_min_months || 6;

    const suppliers = selectedProduct.suppliers || [];
    // A product can have more than one source: a newly approved suggestion
    // pushes a second parentless row rather than replacing the first one.
    // Every parentless row is a source product on some supplier site, not a
    // seller -- render all of them in the header, not just the first.
    const sourceProducts = suppliers.filter(s => !s.parent_url);
    const sellers = suppliers.filter(s => s.parent_url);

    const expirationThreshold = moment().add(expirationMinMonths, "months");

    const [editingMargin, setEditingMargin] = useState(false);
    const [marginInput, setMarginInput] = useState(null);

    // Header: add a new source product.
    const [sourceDialogVisible, setSourceDialogVisible] = useState(false);
    const [sourceUrl, setSourceUrl] = useState("");
    const [sourceUrlError, setSourceUrlError] = useState("");

    // Header: check price. One control for the whole product -- the endpoint
    // always refreshes every source row of it in a single job (Task 19's
    // spider honours product_id), so there is nothing to select per source.
    // The displayed state comes from the backend (fetched on open, polled
    // while pending), never frozen at whatever the triggering click saw --
    // see isSupplierCheckPending and the poll effect below.
    const [lastCheckRequest, setLastCheckRequest] = useState(null);
    const pollTimeoutRef = useRef(null);
    const mountedRef = useRef(true);

    // Row actions: edit fields, add price, delete/deactivate confirmation, price history.
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddPriceModal, setShowAddPriceModal] = useState(false);
    const [newPrice, setNewPrice] = useState(null);
    const [currencies, setCurrencies] = useState([]);
    // {type: 'delete' | 'deactivate', seller}
    const [confirmAction, setConfirmAction] = useState(null);
    const [expandedSeller, setExpandedSeller] = useState(null);
    // Sellers expiring before the shelf-life threshold are hidden unless the
    // user asks to see them.
    const [showExpiringSellers, setShowExpiringSellers] = useState(false);

    // null means "follow the campaign"; 0 is a real override (sell at
    // supplier cost), so this must not use `||`.
    const hasMarginOverride = selectedProduct.supplier_margin_rate !== null && selectedProduct.supplier_margin_rate !== undefined;
    const effectiveMargin = hasMarginOverride ? selectedProduct.supplier_margin_rate : settings.margin_rate;
    // Mirrors VitaminboxSupplierMinPrice.calculate: no margin anywhere (no
    // product override, no campaign margin) means the backend writes NO
    // minimum price at all -- the panel must not mark rows as if it did.
    const hasEffectiveMargin = effectiveMargin !== null && effectiveMargin !== undefined;

    // Cheapest `sellerCount` eligible sellers feed the minimum price.
    const minPriceUrls = hasEffectiveMargin
        ? new Set(
            sellers
                .filter(s => isEligibleForMinPrice(s, expirationThreshold))
                .sort((a, b) => lastPriceOf(a).value - lastPriceOf(b).value)
                .slice(0, sellerCount)
                .map(s => s.product_url)
        )
        : new Set();

    const sortedSellers = [...sellers].sort((a, b) => {
        const av = lastPriceOf(a)?.value;
        const bv = lastPriceOf(b)?.value;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return av - bv;
    });
    const expiringSellerCount = sortedSellers.filter(s => isExpiringSoon(s, expirationThreshold)).length;
    const visibleSellers = showExpiringSellers
        ? sortedSellers
        : sortedSellers.filter(s => !isExpiringSoon(s, expirationThreshold));

    const openMarginEditor = () => {
        setMarginInput(effectiveMargin);
        setEditingMargin(true);
    };

    const saveMarginRate = (value) => {
        productService.updateSupplierMarginRate(selectedProduct.campaign_id, selectedProduct.id, value).then(() => {
            selectedProduct.supplier_margin_rate = value;
            setEditingMargin(false);
            if (props.refreshProducts) props.refreshProducts();
            toast.current.show({severity: "success", summary: t("campaign.supplier_margin_rate"), detail: t("message_detail.successful")});
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    // Distinct from saving a typed 0: this always sends null.
    const clearMarginRate = () => saveMarginRate(null);

    //#region Header: add source / price check
    // Placed on the source product (this header), never per seller row: a
    // seller's url is never fetched on its own -- one parent call returns
    // every seller under its source, which is why the check belongs here.

    const openAddSource = () => {
        setSourceUrl("");
        setSourceUrlError("");
        setSourceDialogVisible(true);
    };

    const submitAddSource = () => {
        const error = urlValidator(sourceUrl, t);
        if (error) {
            setSourceUrlError(error);
            return;
        }
        props.addSupplier(sourceUrl);
        setSourceDialogVisible(false);
        setSourceUrl("");
    };

    // Keeps re-fetching this product's last supplier-check state every
    // SUPPLIER_CHECK_POLL_MS while it is still pending, so the control shows
    // the backend's true state and re-enables itself once the job finishes --
    // instead of freezing on the one-shot value the triggering click first
    // saw. Cancels any previously scheduled poll before scheduling a new one,
    // so a fresh check (or a fresh mount) never ends up with two loops.
    const schedulePoll = (scraping_request) => {
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
        }
        if (!isSupplierCheckPending(scraping_request))
            return;
        pollTimeoutRef.current = setTimeout(() => {
            productService.getLastSupplierCheck(selectedProduct.id).then((fresh) => {
                if (!mountedRef.current) return;
                setLastCheckRequest(fresh || null);
                schedulePoll(fresh);
            }).catch(() => {});
        }, SUPPLIER_CHECK_POLL_MS);
    };

    // On opening the tab (or switching products): show this product's true
    // last-check state immediately, and start polling if it is still pending
    // -- covers a check started from elsewhere (another tab, another admin)
    // as well as a page reload catching an in-flight job.
    useEffect(() => {
        mountedRef.current = true;
        productService.getLastSupplierCheck(selectedProduct.id).then((scraping_request) => {
            if (!mountedRef.current) return;
            setLastCheckRequest(scraping_request || null);
            schedulePoll(scraping_request);
        }).catch(() => {});
        return () => {
            mountedRef.current = false;
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProduct.id]);

    // Always creates a FRESH, product-scoped scraping request (never reruns an
    // old one) -- see checkSupplierPrice in ProductService.js and Task 20's
    // backend fix. This is what makes the control work identically for every
    // source, including one added through the (separate) suggestion-approval
    // flow, which never gets a scraping request of its own: rerunning would
    // have nothing to rerun for that majority case. Immediately reflects the
    // fresh (pending) state and starts polling, rather than waiting for the
    // next scheduled poll to notice it.
    const checkPrice = () => {
        return productService.checkSupplierPrice(selectedProduct.id).then((scraping_request) => {
            setLastCheckRequest(scraping_request || null);
            schedulePoll(scraping_request);
        });
    };

    //#endregion Header

    //#region Row actions: activate/deactivate, delete, edit fields, price add/delete

    const requestToggleActive = (rowData) => {
        // Only the deactivate direction is destructive -- re-activating a row
        // needs no confirmation, mirroring the seller tab.
        if (rowData.is_active) {
            setConfirmAction({type: 'deactivate', seller: rowData});
        } else {
            props.changeSupplierIsActive([rowData.product_url], rowData.is_active);
        }
    };

    const askDelete = (rowData) => setConfirmAction({type: 'delete', seller: rowData});

    const closeConfirm = () => setConfirmAction(null);

    const runConfirmedAction = () => {
        const {type, seller} = confirmAction;
        const result = type === 'delete'
            ? props.deleteSupplier([seller.product_url])
            : props.changeSupplierIsActive([seller.product_url], seller.is_active);
        Promise.resolve(result).then(closeConfirm);
    };

    const openEdit = (rowData) => {
        setSelectedSeller({...rowData, _original_url: rowData.product_url});
        setShowEditModal(true);
    };

    const onSellerFieldChange = (e) => {
        const {key, value} = getKeyValue(e);
        setSelectedSeller({...selectedSeller, [key]: value});
    };

    const submitEdit = () => {
        const newUrl = stripTrackingParams(selectedSeller.product_url);
        props.updateSupplier({
            // competitor_url is the original key used to locate the supplier row;
            // product_url carries the (possibly changed) new url. These key names
            // come straight from the contract -- the shared service function was
            // never renamed for the supplier path.
            competitor_url: selectedSeller._original_url,
            product_url: newUrl,
            multiplier: selectedSeller.multiplier,
            discount_type: selectedSeller.discount_type
        });
        setShowEditModal(false);
        setSelectedSeller(null);
    };

    const openAddPrice = (rowData) => {
        setSelectedSeller(rowData);
        const lastPrice = lastPriceOf(rowData);
        setNewPrice({
            competitor_url: rowData.product_url,
            added_price: lastPrice ? lastPrice.value : 0,
            added_sale_price: lastPrice ? lastPrice.sale_value : 0,
            added_stock: lastPrice ? lastPrice.stock : 1,
            added_currency: lastPrice ? lastPrice.currency : null,
            added_date: new Date()
        });
        if (currencies.length === 0) {
            userService.getCurrencies().then(data => setCurrencies(data));
        }
        setShowAddPriceModal(true);
    };

    const onNewPriceChange = (e) => {
        const {key, value} = getKeyValue(e);
        setNewPrice({...newPrice, [key]: value});
    };

    const submitAddPrice = () => {
        productService.addSupplierPrice(selectedProduct.id, newPrice).then((data) => {
            setShowAddPriceModal(false);
            selectedSeller.prices.push(data);
            setSelectedSeller(null);
            setNewPrice(null);
            toast.current.show({severity: 'success', summary: t('supplier.edit_seller'), detail: t('message_detail.successful')});
        }).catch(error => {
            setShowAddPriceModal(false);
            setError(error);
            setError(null);
        });
    };

    const deleteSellerPrice = (priceRow, competitor_url) => {
        productService.deleteSupplierPrice(selectedProduct.id, {competitor_url: competitor_url, created_at: priceRow.created_at}).then(() => {
            toast.current.show({severity: 'success', summary: t('supplier.edit_seller'), detail: t('message_detail.successful')});
            if (props.refreshProducts) props.refreshProducts();
        }).catch(error => {
            setError(error);
            setError(null);
        });
    };

    //#endregion Row actions

    const sellerNameTemplate = (rowData) => (
        <div>
            <span className="p-column-title">{t("table_headers.name")}</span>
            <a href={rowData.product_url} target="_blank" rel="noopener noreferrer">
                {rowData.name && rowData.name !== "" ? rowData.name : getHostName(rowData.product_url)}
            </a>
            {minPriceUrls.has(rowData.product_url) &&
                <span className="p-tag" style={{
                    marginLeft: ".5em", background: "#dcfce7", color: "#15803d",
                    padding: ".15em .5em", borderRadius: "6px", fontSize: ".75rem"}}>
                    {t("supplier.feeds_min_price")}
                </span>}
        </div>
    );

    const priceTemplate = (rowData) => {
        const lastPrice = lastPriceOf(rowData);
        return <div className="p-text-nowrap p-text-right">
            <span className="p-column-title">{t("product_detail.price")}</span>
            {lastPrice ? formatCurrency(lastPrice.value, lastPrice.currency) : "-"}
        </div>;
    };

    const stockTemplate = (rowData) => {
        const lastPrice = lastPriceOf(rowData);
        return <div className="p-text-nowrap p-text-center">
            <span className="p-column-title">{t("product_detail.stock")}</span>
            {SupplierStockTemplate(lastPrice && lastPrice.stock)}
        </div>;
    };

    const expirationTemplate = (rowData) => {
        const expiration = parseExpiration(rowData.expiration);
        return <div className="p-text-nowrap p-text-center">
            <span className="p-column-title">{t("supplier.expiration")}</span>
            {expiration ? expiration.format("MMM YYYY") : "-"}
        </div>;
    };

    const lastUpdateTemplate = (rowData) => {
        const lastPrice = lastPriceOf(rowData);
        return <div className="p-text-nowrap">
            <span className="p-column-title">{t("table_headers.last_check")}</span>
            {lastPrice && toDateTimeString(lastPrice.created_at)}
        </div>;
    };

    const actionTemplate = (rowData) => (
        <div className="p-text-nowrap" style={{display: "flex", alignItems: "center", gap: 4}}>
            <span className="p-column-title">{t("table_headers.action")}</span>
            <InputSwitch checked={rowData.is_active} onChange={() => requestToggleActive(rowData)}
                         tooltip={rowData.is_active ? t('actions.deactivate') : t('actions.activate')}/>
            <Button icon="pi pi-pencil" className="p-button-text p-button-rounded p-button-sm"
                    tooltipOptions={{position: 'bottom'}} tooltip={t('actions.edit')} onClick={() => openEdit(rowData)}/>
            <Button icon="pi pi-trash" className="p-button-text p-button-rounded p-button-sm p-button-danger"
                    tooltipOptions={{position: 'bottom'}} tooltip={t('actions.remove')} onClick={() => askDelete(rowData)}/>
        </div>
    );

    const priceDeleteTemplate = (rowData, columnProps) => (
        <Button icon="pi pi-trash" className="p-button-outlined p-button-rounded p-button-danger p-mr-1"
                tooltipOptions={{position: 'bottom'}} tooltip={t('actions.remove')}
                onClick={() => deleteSellerPrice(rowData, columnProps.field)}/>
    );

    const sellerRowExpansionTemplate = (rowData) => (
        <div className="p-card p-col-12 p-mt-1">
            <div className="p-grid p-col-12 p-nogutter">
                <div className="p-col-11 p-text-bold"><h5>{t('product_detail.last_checks')}</h5></div>
                <div className="p-col-1" style={{textAlign: 'right'}}>
                    <Button icon="pi pi-plus" className="p-button-outlined p-button-rounded p-button-success p-mr-1"
                            tooltipOptions={{position: 'bottom'}} tooltip={t('actions.add_price')}
                            onClick={() => openAddPrice(rowData)}/>
                </div>
            </div>
            <DataTable value={rowData.prices} paginator={true} rows={5} responsive={true}
                       sortField="created_at" sortOrder={-1}>
                <Column field="created_at" header={t('product_detail.date')} body={(p) => toDateTimeString(p.created_at)}/>
                <Column field="value" header={t('product_detail.price')} headerStyle={{textAlign: "right"}}
                        body={(p) => formatCurrency(p.value, p.currency)}/>
                <Column field="sale_value" header={t('product.list_price')} headerStyle={{textAlign: "right"}}
                        body={(p) => formatCurrency(p.sale_value, p.currency)}/>
                <Column field="stock" header={t('product_detail.stock')} headerStyle={{textAlign: "center"}}
                        body={(p) => SupplierStockTemplate(p.stock)}/>
                <Column body={priceDeleteTemplate} field={rowData.product_url} style={{textAlign: 'center', width: "8em"}}/>
            </DataTable>
        </div>
    );

    const confirmYesNo = confirmAction &&
        <div>
            <Button label={t('yes_no.no')} icon="pi pi-times" className="p-button-warning" onClick={closeConfirm}/>
            <Button label={t('yes_no.yes')} icon="pi pi-check" className="p-button-danger" onClick={runConfirmedAction}/>
        </div>;

    return (
        <React.Fragment>
            <div style={{
                border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16,
                display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12}}>
                <div>
                    <div style={{display: "flex", alignItems: "center", gap: 6}}>
                        <Button label={t("supplier.add_source")} icon="pi pi-plus"
                                className="p-button-outlined p-button-success p-button-sm" onClick={openAddSource}/>
                        {/* One control for the whole product: the backend always
                            refreshes every source row in a single job, so there is
                            nothing to select per source. Works identically whether a
                            source was added manually or via an approved suggestion --
                            it always creates a fresh request rather than rerunning one
                            that a suggestion-approved source may never have had. */}
                        <ScrapeStatus type="price" scraping_request={lastCheckRequest}
                                      campaign_id={selectedProduct.campaign_id}
                                      product_id={selectedProduct.id}
                                      competitor_url={null}
                                      onRun={checkPrice}
                                      newRequestCompleted={props.refreshProducts}/>
                    </div>
                    {sourceProducts.map((source, i) => (
                        <div key={source.product_url || i}>
                            <a href={source.product_url} target="_blank" rel="noopener noreferrer">
                                {source.product_name || source.name || getHostName(source.product_url)}
                            </a>
                        </div>
                    ))}
                </div>
                <div style={{display: "flex", alignItems: "center", gap: 8}}>
                    <span style={{color: "#6b7280"}}>{t("campaign.supplier_margin_rate")}:</span>
                    {editingMargin
                        ? <React.Fragment>
                            <InputNumber value={marginInput} mode="decimal" minFractionDigits={0} maxFractionDigits={2}
                                         style={{width: "6em"}} onValueChange={(e) => setMarginInput(e.value)}/>
                            <Button icon="pi pi-check" className="p-button-text p-button-success p-button-sm"
                                    tooltip={t("actions.save")} onClick={() => saveMarginRate(marginInput)}/>
                            <Button icon="pi pi-times" className="p-button-text p-button-secondary p-button-sm"
                                    tooltip={t("actions.cancel")} onClick={() => setEditingMargin(false)}/>
                          </React.Fragment>
                        : <React.Fragment>
                            <b>{formatDecimal(effectiveMargin)}%</b>
                            <span style={{fontSize: ".75rem", color: "#9ca3af"}}>
                                ({hasMarginOverride ? t("supplier.margin_source_product") : t("supplier.margin_source_campaign")})
                            </span>
                            <Button icon="pi pi-pencil" className="p-button-text p-button-sm"
                                    tooltip={t("actions.edit")} onClick={openMarginEditor}/>
                            {hasMarginOverride &&
                                <Button icon="pi pi-replay" className="p-button-text p-button-sm p-button-secondary"
                                        tooltip={t("supplier.margin_clear")} onClick={clearMarginRate}/>}
                          </React.Fragment>}
                </div>
            </div>

            {!hasEffectiveMargin &&
                <div style={{
                    background: "#fef3c7", color: "#92400e", borderRadius: 8, padding: "8px 12px",
                    marginBottom: 12, fontSize: ".85rem"}}>
                    {t("supplier.no_margin_no_min_price")}
                </div>}

            {expiringSellerCount > 0 &&
                <div style={{
                    display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
                    color: "#6b7280", fontSize: ".85rem"}}>
                    <i className={"pi " + (showExpiringSellers ? "pi-eye" : "pi-eye-slash")}/>
                    <span>
                        {t(showExpiringSellers ? "supplier.expiring_shown" : "supplier.expiring_hidden",
                           {number: expiringSellerCount, months: expirationMinMonths})}
                    </span>
                    <Button label={t(showExpiringSellers ? "supplier.hide_expiring" : "supplier.show_expiring")}
                            className="p-button-text p-button-sm"
                            onClick={() => setShowExpiringSellers(!showExpiringSellers)}/>
                </div>}

            <DataTable value={visibleSellers} responsive={true} dataKey="product_url"
                       expandedRows={expandedSeller}
                       onRowToggle={(e) => setExpandedSeller(e.data)}
                       rowExpansionTemplate={sellerRowExpansionTemplate}>
                <Column expander={true} style={{width: "3em"}}/>
                <Column body={IndexTemplate} headerStyle={{width: "3em"}}/>
                <Column body={sellerNameTemplate} header={t("table_headers.name")}/>
                <Column body={priceTemplate} header={t("product_detail.price")} headerStyle={{textAlign: "right"}}/>
                <Column body={stockTemplate} header={t("product_detail.stock")} headerStyle={{textAlign: "center"}}/>
                <Column body={expirationTemplate} header={t("supplier.expiration")} headerStyle={{textAlign: "center"}}/>
                <Column body={lastUpdateTemplate} header={t("table_headers.last_check")}/>
                <Column body={actionTemplate} header={t("table_headers.action")} className="preventSelect"/>
            </DataTable>

            <Dialog visible={sourceDialogVisible} header={t("supplier.add_source")}
                    style={{maxWidth: '90vw', width: '28em'}} modal={true} onHide={() => setSourceDialogVisible(false)}>
                <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 16px'}}>{t("supplier.add_source_hint")}</p>
                <label htmlFor="source_url" style={{display: 'block', fontWeight: 600, fontSize: '.85rem', marginBottom: 6}}>
                    {t("supplier.source_url")}
                </label>
                <InputText id="source_url" value={sourceUrl}
                           onChange={(e) => setSourceUrl(e.target.value)}
                           onKeyDown={(e) => { if (e.key === "Enter") submitAddSource(); }}
                           placeholder="https://..." className={sourceUrlError.length > 0 ? "p-invalid" : ""}
                           style={{width: '100%'}} autoFocus={true}/>
                {sourceUrlError.length > 0 &&
                    <Message severity="error" text={sourceUrlError} style={{marginTop: 10, width: '100%', justifyContent: 'flex-start'}}/>}
                <div style={{display: 'flex', gap: 10, marginTop: 20}}>
                    <Button label={t('actions.cancel')} className="p-button-text p-button-secondary" style={{flex: 1}}
                            onClick={() => setSourceDialogVisible(false)}/>
                    <Button label={t('actions.add')} icon="pi pi-plus" className="p-button-success" style={{flex: 1}}
                            onClick={submitAddSource}/>
                </div>
            </Dialog>

            {selectedSeller && showEditModal &&
                <Dialog visible={true} header={t('supplier.edit_seller') + ' - ' + (selectedSeller.name || getHostName(selectedSeller.product_url))}
                        style={{maxWidth: '60vw', minWidth: '30vw'}} modal={true}
                        onHide={() => {setShowEditModal(false); setSelectedSeller(null);}}>
                    <div className="p-grid p-fluid alignCenter">
                        <div className="p-col-12">
                            <label htmlFor="product_url" style={{display: 'block', marginBottom: '.35em'}}>{t('supplier.seller_url')}</label>
                            <InputText id="product_url" name="product_url" value={selectedSeller.product_url || ''} style={{width: "100%"}}
                                       onChange={onSellerFieldChange}/>
                        </div>
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputNumber id="multiplier" name="multiplier" value={selectedSeller.multiplier} style={{width: "200px"}}
                                             mode="decimal" minFractionDigits={1} onValueChange={onSellerFieldChange}/>
                                <label htmlFor="multiplier">{t('product_detail.multiplier')}</label>
                            </span>
                        </div>
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputText id="discount_type" name="discount_type" value={selectedSeller.discount_type} style={{width: "200px"}}
                                           onChange={onSellerFieldChange}/>
                                <label htmlFor="discount_type">{t('product.discount_type')}</label>
                            </span>
                        </div>
                        <div className="p-col-6">
                            <Button label={t('actions.cancel')} icon="pi pi-angle-double-left" className="p-button-outlined p-button-warning"
                                    onClick={() => {setShowEditModal(false); setSelectedSeller(null);}}/>
                        </div>
                        <div className="p-col-6">
                            <Button label={t('actions.save')} icon="pi pi-save" className="p-button-outlined p-button-success" onClick={submitEdit}/>
                        </div>
                    </div>
                </Dialog>}

            {newPrice && showAddPriceModal &&
                <Dialog visible={true} header={t('supplier.edit_seller') + ' - ' + (selectedSeller.name || getHostName(selectedSeller.product_url))}
                        style={{maxWidth: '60vw', minWidth: '30vw'}} modal={true}
                        onHide={() => {setShowAddPriceModal(false); setNewPrice(null);}}>
                    <div className="p-grid p-fluid alignCenter">
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputNumber id="added_price" name="added_price" value={newPrice.added_price} style={{width: "200px"}}
                                             mode="decimal" minFractionDigits={1} onValueChange={onNewPriceChange}/>
                                <label htmlFor="added_price">{t('product_detail.price')}</label>
                            </span>
                        </div>
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputNumber id="added_sale_price" name="added_sale_price" value={newPrice.added_sale_price} style={{width: "200px"}}
                                             mode="decimal" minFractionDigits={1} onValueChange={onNewPriceChange}/>
                                <label htmlFor="added_sale_price">{t('product.list_price')}</label>
                            </span>
                        </div>
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <Dropdown id="added_currency" name="added_currency" value={newPrice.added_currency} style={{width: "200px"}}
                                          options={currencies} filter showClear onChange={onNewPriceChange}/>
                                <label htmlFor="added_currency">{t('campaign.currency')}</label>
                            </span>
                        </div>
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <InputNumber id="added_stock" name="added_stock" value={newPrice.added_stock ? newPrice.added_stock : 0}
                                             style={{width: "200px"}} mode="decimal" minFractionDigits={0} onValueChange={onNewPriceChange}/>
                                <label htmlFor="added_stock">{t('product.stock')}</label>
                            </span>
                        </div>
                        <div className="p-col-12">
                            <span className="p-float-label">
                                <Calendar id="added_date" name="added_date" readOnlyInput={true} value={newPrice.added_date}
                                          onChange={onNewPriceChange} showIcon={true} dateFormat="yy-mm-dd" style={{width: "200px"}}/>
                                <label htmlFor="added_date">{t('product_detail.date')}</label>
                            </span>
                        </div>
                        <div className="p-col-6">
                            <Button label={t('actions.cancel')} icon="pi pi-angle-double-left" className="p-button-outlined p-button-warning"
                                    onClick={() => {setShowAddPriceModal(false); setNewPrice(null);}}/>
                        </div>
                        <div className="p-col-6">
                            <Button label={t('actions.save')} icon="pi pi-save" className="p-button-outlined p-button-success" onClick={submitAddPrice}/>
                        </div>
                    </div>
                </Dialog>}

            {confirmAction &&
                <Dialog header={t(confirmAction.type === 'delete' ? 'supplier.delete_confirm_title' : 'supplier.deactivate_confirm_title')}
                        footer={confirmYesNo} visible={true} style={{maxWidth: '50vw', minWidth: '20vw'}} modal={true} onHide={closeConfirm}>
                    <Message severity="warn" className="p-text-left"
                             text={t(confirmAction.type === 'delete' ? 'supplier.delete_confirm_msg' : 'supplier.deactivate_confirm_msg',
                                     {seller: confirmAction.seller.name || getHostName(confirmAction.seller.product_url)})}/>
                </Dialog>}
        </React.Fragment>
    );
};

export default Supplier;
